import {
	FloatingFocusManager,
	FloatingPortal,
	autoUpdate,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import React, { useState } from "react";

import { faGear } from "@performyard/fontawesome/pro-solid-svg-icons";
import type { MeetingStatus } from "@performyard/models";
import { ButtonStandard, Text } from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";
import type { GetUserCalendarIntegrationStatusResponse } from "@performyard/service-contracts";

import { useEditWorkspaceMeeting } from "../../../../../api/hooks";
import {
	useCompanyCalendarIntegrationSettings,
	useUserCalendarIntegrationStatus,
} from "../../../../../api/hooks/defs/calendar-integration";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

import { StatusConfirmationModal } from "./modals";

interface MeetingStatusDropdownProps {
	meeting: MeetingModelJson;
	userId: string;
	disabled?: boolean;
	setEditingMeeting?: (meeting: MeetingModelJson) => void;
}

const MeetingStatusDropdown = ({
	meeting,
	userId,
	disabled = false,
	setEditingMeeting,
}: MeetingStatusDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const { mutate: editMeeting } = useEditWorkspaceMeeting();

	const [modalState, setModalState] = useState<
		| { type: "none" }
		| { type: "remove-from-calendar"; isLoading: boolean }
		| { type: "status-change"; isLoading: boolean; status: MeetingStatus }
	>({ type: "none" });

	// Calendar integration state
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	const { data: companyCalendarIntegrationSettings } = useCompanyCalendarIntegrationSettings({
		enabled: ffIsCalendarIntegrationEnabled,
	});
	const { data: userCalendarStatus }: { data: GetUserCalendarIntegrationStatusResponse | undefined } =
		useUserCalendarIntegrationStatus({
			enabled: ffIsCalendarIntegrationEnabled && companyCalendarIntegrationSettings?.enabled === true,
		});

	// Determine if calendar integration is enabled for this user
	const isCalendarIntegrationEnabled =
		ffIsCalendarIntegrationEnabled &&
		companyCalendarIntegrationSettings?.enabled &&
		userCalendarStatus?.isConnected &&
		["google", "outlook"].includes(userCalendarStatus?.provider || "") &&
		userCalendarStatus?.selectedCalendarId;

	// Check if this meeting has a calendar event
	const hasCalendarEvent = Boolean(meeting.calendarEvent?.eventId);

	// Determine if there's a calendar mismatch (user switched calendars after meeting was created)
	const isCalendarMismatch =
		meeting.calendarEvent?.eventId && meeting.calendarEvent?.provider && userCalendarStatus
			? !(
					userCalendarStatus.provider === meeting.calendarEvent.provider &&
					userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId
				)
			: false;

	// Only meeting creator can change status
	const canChangeStatus = meeting.created?.userId === userId && !disabled;
	// Only allow editing if meeting is active and user is creator
	const canEdit = meeting.status === "active" && meeting.created?.userId === userId && !disabled;

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(8), flip({ fallbackAxisSideDirection: "end" }), shift()],
		whileElementsMounted: autoUpdate,
		placement: "bottom-end",
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	// Dynamic options based on current meeting status and permissions
	const getOptions = () => {
		const options: { id: string; label: string; action: "edit" | "status" | "calendar" }[] = [];

		// "Edit Meeting" is the first option when user can edit
		if (canEdit && setEditingMeeting) {
			options.push({ id: "edit", label: "Edit Meeting", action: "edit" });
		}

		// Calendar integration options
		if (isCalendarIntegrationEnabled && canEdit) {
			if (hasCalendarEvent && !isCalendarMismatch) {
				// Meeting has a calendar event and user is connected to the same calendar, allow removal
				options.push({ id: "remove-from-calendar", label: "Remove from Calendar", action: "calendar" });
			} else {
				// Meeting has never been connected to user's integrated calendar or was previously disconnected
				options.push({ id: "add-to-calendar", label: "Add to Calendar", action: "calendar" });
			}
		}

		if (canChangeStatus) {
			// "Reopen Meeting" is an option when looking at non-active meetings
			if (meeting.status !== "active") {
				options.push({ id: "active", label: "Reopen Meeting", action: "status" });
			}

			// "Complete Meeting" is an option when looking at active, archived, or canceled meetings
			if (["active", "archived", "canceled"].includes(meeting.status)) {
				options.push({ id: "completed", label: "Complete Meeting", action: "status" });
			}

			// "Archive Meeting" is an option when looking at non-archived meetings
			if (meeting.status !== "archived") {
				options.push({ id: "archived", label: "Archive Meeting", action: "status" });
			}

			// "Cancel Meeting" is an option when looking at active meetings
			if (meeting.status === "active") {
				options.push({ id: "canceled", label: "Cancel Meeting", action: "status" });
			}
		}

		return options;
	};

	const options = getOptions();

	// Don't render if no options available
	if (options.length === 0) {
		return null;
	}

	const handleOptionClick = (option: { id: string; label: string; action: "edit" | "status" | "calendar" }) => {
		setIsOpen(false);

		if (option.action === "edit") {
			setEditingMeeting?.(meeting);
		} else if (option.action === "calendar") {
			if (option.id === "add-to-calendar") {
				// Add calendar event for this meeting
				editMeeting({
					meetingId: meeting._id,
					data: {
						addToCalendar: true,
					},
				});
			} else if (option.id === "remove-from-calendar") {
				// Show confirmation modal before removing
				setModalState({ type: "remove-from-calendar", isLoading: false });
			}
		} else if (option.action === "status") {
			const status = option.id as MeetingStatus;

			// Show confirmation modal for cancel, complete, and archive actions
			if (["canceled", "completed", "archived"].includes(status)) {
				setModalState({ type: "status-change", isLoading: false, status });
			} else {
				// For other status changes, update without confirmation
				editMeeting({ meetingId: meeting._id, data: { status } });
			}
		}
	};

	const handleConfirmStatusChange = (removeFromCalendar?: boolean) => {
		if (modalState.type === "status-change" && modalState.status) {
			setModalState({ type: "status-change", isLoading: true, status: modalState.status });

			// Determine what data to send based on status and calendar options
			const updateData: { status: MeetingStatus; removeFromCalendar?: boolean } = { status: modalState.status };

			// If cancelling and removing from calendar, set removeFromCalendar flag
			if (modalState.status === "canceled" && removeFromCalendar) {
				updateData.removeFromCalendar = true;
			}

			editMeeting(
				{ meetingId: meeting._id, data: updateData },
				{
					onSuccess: () => {
						setModalState({ type: "none" });
					},
					onError: () => {
						setModalState({ type: "status-change", isLoading: false, status: modalState.status });
					},
				}
			);
		}
	};

	const handleRemoveFromCalendar = () => {
		setModalState({ type: "remove-from-calendar", isLoading: true });

		// Always remove from calendar when this action is chosen
		const updateData = {
			removeFromCalendar: true,
		};

		editMeeting(
			{
				meetingId: meeting._id,
				data: updateData,
			},
			{
				onSuccess: () => {
					setModalState({ type: "none" });
				},
				onError: () => {
					setModalState({ type: "remove-from-calendar", isLoading: false });
				},
			}
		);
	};

	return (
		<>
			<div>
				<ButtonStandard
					variant="outline"
					color="neutral"
					size="small"
					iconLeft={faGear}
					ref={refs.setReference}
					onClick={() => setIsOpen(!isOpen)}
					{...getReferenceProps()}
				>
					Options
				</ButtonStandard>
			</div>

			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context} modal={false}>
						<div
							className="rounded bg-neutral-0 shadow-lg outline outline-1 outline-neutral-3"
							style={{ width: 140, ...floatingStyles, zIndex: 1000 }}
							{...getFloatingProps()}
							ref={refs.setFloating}
						>
							{options.map((option, index) => (
								<button
									key={option.id}
									onClick={() => handleOptionClick(option)}
									className={`w-full cursor-pointer border-0 bg-neutral-0 px-2 py-1 text-left text-neutral-4 hover:bg-neutral-2 hover:text-neutral-9 ${
										index === 0 ? "rounded-t" : ""
									} ${index === options.length - 1 ? "rounded-b" : ""}`}
								>
									<Text size="caption" weight="regular">
										{option.label}
									</Text>
								</button>
							))}
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}

			<StatusConfirmationModal
				isOpen={modalState.type === "status-change" || modalState.type === "remove-from-calendar"}
				isLoading={
					(modalState.type === "status-change" || modalState.type === "remove-from-calendar") &&
					modalState.isLoading
				}
				status={modalState.type === "status-change" ? modalState.status : null}
				action={modalState.type === "remove-from-calendar" ? "remove-from-calendar" : "status-change"}
				onClose={() => setModalState({ type: "none" })}
				onConfirm={
					modalState.type === "remove-from-calendar" ? handleRemoveFromCalendar : handleConfirmStatusChange
				}
				hasConnectedCalendarEvent={
					hasCalendarEvent &&
					!isCalendarMismatch &&
					(modalState.type === "remove-from-calendar" ||
						(modalState.type === "status-change" && modalState.status === "canceled"))
				}
			/>
		</>
	);
};

export default MeetingStatusDropdown;
